import { collection, getDocs, addDoc, updateDoc, deleteDoc, query, orderBy, getDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { getAuth } from 'firebase/auth';

const UPDATES_COLLECTION = 'updates';

// Get all updates ordered by creation date (newest first)
export const getAllUpdates = async () => {
  try {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to get updates');
    }

    // Determine user type by checking which collection they exist in
    const internalUserDoc = await getDoc(doc(db, 'internal_users', auth.currentUser.uid));
    const isInternalUser = internalUserDoc.exists();

    const updatesRef = collection(db, UPDATES_COLLECTION);
    const q = query(
      updatesRef,
      orderBy('createdAt', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    const updates = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate?.() || doc.data().createdAt
    }));

    // Filter updates based on user type and target audience
    const filteredUpdates = updates.filter(update => {
      // Internal users see internal updates and general updates
      if (isInternalUser) {
        return update.targetAudience === 'internal' || 
               update.targetAudience === 'all' || 
               !update.targetAudience; // Backward compatibility
      } else {
        // Portal users see portal updates and general updates
        return update.targetAudience === 'portal' || 
               update.targetAudience === 'all' || 
               !update.targetAudience; // Backward compatibility
      }
    });

    return filteredUpdates;
  } catch (error) {
    console.error('Error getting updates:', error);
    throw error;
  }
};

// Get recent updates with limit
export const getRecentUpdates = async (limitCount = 10) => {
  try {
    const allUpdates = await getAllUpdates();
    return allUpdates.slice(0, limitCount);
  } catch (error) {
    console.error('Error getting recent updates:', error);
    throw error;
  }
};

// Create a new update
export const createUpdate = async (updateData) => {
  try {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to create updates');
    }

    // Get user info
    const internalUserDoc = await getDoc(doc(db, 'internal_users', auth.currentUser.uid));
    const userData = internalUserDoc.exists() ? internalUserDoc.data() : null;
    const userName = userData 
      ? `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || auth.currentUser.displayName || auth.currentUser.email
      : auth.currentUser.displayName || auth.currentUser.email;

    const updatesRef = collection(db, UPDATES_COLLECTION);
    const newUpdate = {
      title: updateData.title,
      description: updateData.description,
      department: updateData.department || 'General',
      type: updateData.type || 'Available Now',
      createdBy: userName,
      createdByUid: auth.currentUser.uid,
      createdAt: serverTimestamp(),
      targetAudience: 'all' // Keep for backward compatibility, defaults to all
    };
    
    // Add quarter and year if type is "Future"
    if (updateData.type === 'Future') {
      newUpdate.quarter = updateData.quarter;
      newUpdate.year = updateData.year;
    }
    
    const docRef = await addDoc(updatesRef, newUpdate);
    
    return {
      id: docRef.id,
      ...newUpdate
    };
  } catch (error) {
    console.error('Error creating update:', error);
    throw error;
  }
};

// Update an existing update
export const updateUpdate = async (updateId, updateData) => {
  try {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to update updates');
    }

    const updateRef = doc(db, UPDATES_COLLECTION, updateId);
    const updateFields = {
      title: updateData.title,
      description: updateData.description,
      department: updateData.department || 'General',
      type: updateData.type || 'Available Now'
    };
    
    // Add or remove quarter and year based on type
    if (updateData.type === 'Future') {
      updateFields.quarter = updateData.quarter;
      updateFields.year = updateData.year;
    } else {
      // Remove quarter and year if changing from Future to Available Now
      updateFields.quarter = null;
      updateFields.year = null;
    }
    
    await updateDoc(updateRef, updateFields);
    
    return {
      id: updateId,
      ...updateFields
    };
  } catch (error) {
    console.error('Error updating update:', error);
    throw error;
  }
};

// Delete an update
export const deleteUpdate = async (updateId) => {
  try {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to delete updates');
    }

    const updateRef = doc(db, UPDATES_COLLECTION, updateId);
    await deleteDoc(updateRef);
    
    return updateId;
  } catch (error) {
    console.error('Error deleting update:', error);
    throw error;
  }
};

